import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { FinancingStagePath, financingStageQueryOptions, useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";
import { addError } from "@wayflyer/blackbox-fe";

import { useCreateFundingApplicationMutation } from "../../queries/funding-application/queries";

import { LandingScreen } from "./landing";

export const FundingLandingPage = () => {
  const {
    data: { customerUuid, customerDomain },
  } = useCustomerAppConfig();
  const { data: financingStage } = useSuspenseQuery(financingStageQueryOptions(customerUuid));

  // Standard navigate for cross-stage navigation
  const standardNavigate = useNavigate();
  // SPA navigate for within-app navigation
  const { navigate: spaNavigate } = useSpaNavigate("FundingApp");

  const createFundingApplication = useCreateFundingApplicationMutation(customerDomain, customerUuid);

  const onSubmitAdvanceRequest = () => {
    // Create the new funding application. Navigation is handled in the mutation's onSuccess callback
    return createFundingApplication
      .mutateAsync()
      .then(({ data }) => {
        if (data?.subStage) {
          spaNavigate(data.subStage);
        }
      })
      .catch((error) => {
        // Error is already handled by the mutation's onError callback
        // This catch prevents unhandled promise rejection warnings
        addError("Failed to create funding application:", error);
      });
  };

  // Handle initial page load navigation only
  useEffect(() => {
    // Case 1: Wrong stage - redirect to appropriate page using standard navigate
    if (financingStage.stage !== "funding_application") {
      const path = [FinancingStagePath[financingStage.stage], financingStage.subStage].filter(Boolean).join("");
      const to = financingStage.stage === "company_creation" ? `/${path}` : `/${customerDomain}/${path}`;
      standardNavigate(to, { replace: true });
      return;
    }

    // Case 2: In-progress application - continue where they left off using SPA navigate
    if (financingStage.subStage) {
      spaNavigate(financingStage.subStage);
      return;
    }

    // Case 3: No subStage - stay on landing page (no action needed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financingStage?.stage, financingStage?.subStage]);

  // Render LandingScreen by default
  return financingStage?.stage === "funding_application" && !financingStage.subStage ? (
    <LandingScreen onSubmit={onSubmitAdvanceRequest} isPending={createFundingApplication.isPending} />
  ) : null;
};
